import React from "react"
import { useSelector, useDispatch } from "react-redux"

import { MtDriveVersion } from "server/models"
import {
    StoreType,
    addModal,
    getFile,
    getFileLogo,
    createFileName,
    isRecycleOpened,
    convertFileSize,
    setDriveVersionSelected,
} from "client/store"
import { qidIsOpened } from "client/utils"
import {
    Link,
    Logo,
    UserIcon,
    DateFormatter,
    TableColumn,
} from "client/components"

import RevisionEdit from "../revisionEdit"

const Columns = (editFileAccess: boolean) => {
    const file = useSelector((state: StoreType) => state.drive.file.fileEdit)
    const drivePath = useSelector((state: StoreType) => state.drive.drivePath)
    const onSelectVersion = useSelector((state: StoreType) => state.drive.onSelectVersion)

    const userList = useSelector((state: StoreType) => state.user.userList)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const qidPageOpened = qidIsOpened()

    const selectVersion = (version: MtDriveVersion) => {
        if (!onSelectVersion) return
        dispatch(setDriveVersionSelected([version.id]))
    }

    const columns: TableColumn[] = [
        {
            field: "id",
            header: lang.id,
            sortable: true,
            onClick: selectVersion,
        },
        {
            field: "logo",
            header: lang.logo,
            centerAlign: true,
            onClick: selectVersion,
            formatter: (version: MtDriveVersion) => {
                const fileHref = getFile(file, version).download
                const logoHref = getFileLogo(file, version)
                return (
                    <a href={fileHref} target="_blank" rel="noreferrer" title={lang.download}>
                        <Logo table logo={logoHref} alt={lang.download} />
                    </a>
                )
            },
        },
        {
            field: "name",
            header: lang.name,
            sortable: true,
            cellMinWidth: "200px",
            cellMaxWidth: "400px",
            onClick: selectVersion,
            formatter: (version: MtDriveVersion) => {
                const name = version.name || createFileName(file)
                const recycleOpened = isRecycleOpened(drivePath)

                if (recycleOpened) return name
                return (
                    <Link
                        href="/#"
                        onClick={() => {
                            dispatch(addModal({
                                key: "editVersion",
                                content: <RevisionEdit versionId={version.id} />,
                            }))
                        }}
                    >
                        {name}
                    </Link>
                )
            },
        },
        {
            field: "revision",
            header: lang.revision,
            sortable: true,
            cellMinWidth: "100px",
            cellMaxWidth: "300px",
            onClick: selectVersion,
        },
    ]

    if (qidPageOpened) {
        columns.push(
            {
                field: "publicationDate",
                header: lang.publicationDate,
                sortable: true,
                cellMinWidth: "200px",
                onClick: selectVersion,
                formatter: (version: MtDriveVersion) => <DateFormatter date={version.publicationDate} showTime={false} />,
            },
            {
                field: "default",
                header: lang.default,
                sortable: true,
                cellMinWidth: "100px",
                onClick: selectVersion,
                formatter: (version: MtDriveVersion) => (version.default ? lang.yes : lang.no),
            },
            {
                field: "reportNumber",
                header: lang.reportNumber,
                sortable: true,
                cellMinWidth: "150px",
                cellMaxWidth: "300px",
                onClick: selectVersion,
            },
        )
    }

    columns.push(
        {
            field: "owner",
            header: lang.owner,
            cellMinWidth: "150px",
            onClick: selectVersion,
            formatter: (version: MtDriveVersion) => {
                const user = userList[version.owner]
                return <UserIcon user={user} size="sm" />
            },
        },
        {
            field: "createdAt",
            header: lang.created,
            sortable: true,
            onClick: selectVersion,
            formatter: (version: MtDriveVersion) => <DateFormatter date={version.createdAt} />,
        },
        {
            field: "size",
            header: lang.size,
            sortable: true,
            cellMinWidth: "100px",
            onClick: selectVersion,
            formatter: (version: MtDriveVersion) => convertFileSize(version.size),
        },
    )

    if (editFileAccess) {
        columns.push({
            field: "delete",
            centerAlign: true,
            deleteAccess: (version: MtDriveVersion) => (
                version.owner === userData.userId
                && version.accountId === userData.accountId
            ),
        })
    }

    return columns
}
export default Columns
